import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { Media } from '@eiq/files/models';
import { GetNextId } from '@eiq/shared/utils';

import { Command, Condition, QuestionType, QuestionPrefix, CustomTerm } from '@empathy/survey/base';
import { Question, MediaForm } from '@empathy/survey-core';

import { IQuestionForm } from './question-form-group.interface';
import { ConditionForm } from '../conditions/condition-form.model';

export class QuestionForm {
  form = new FormGroup<IQuestionForm>({
    id: new FormControl(''),
    lastOptionIndex: new FormControl<number>(0, { nonNullable: true }),
    code: new FormControl('', Validators.pattern('[a-zA-Z0-9]*')),
    type: new FormControl<QuestionType>(null),
    title: new FormControl(),
    description: new FormControl(),
    pageId: new FormControl(''),
    stimulus: new FormArray([]),
    programmerNotes: new FormControl(),
    active: new FormControl(false),
    requiredInd: new FormControl(true),
    visibleWhen: new FormArray([]),
    requireMediaCompletionInd: new FormControl<boolean>(false),
    minViewTimeSeconds: new FormControl<number>(0),
    autoplayMediaInd: new FormControl<boolean>(false)
  });

  initEmpty(nextId: number, pageId: string): void {
    this.form.controls.lastOptionIndex.setValue(1);
    const id = `${QuestionPrefix.Question}${nextId}`;
    this.form.patchValue({ id: id, code: id, title: `Question ${nextId}`, pageId });
  }

  initHiddenEmpty(nextId: number): void {
    this.form.controls.lastOptionIndex.setValue(1);
    const id = `${QuestionPrefix.HiddenQuestion}${nextId}`;
    this.form.patchValue({ id: id, code: id, title: `Hidden Question ${nextId}` });
  }

  init(question: Question): void {
    this.form.patchValue(question);
    this.form.controls.lastOptionIndex.setValue(Math.max(question.lastOptionIndex ?? 0, Number(GetNextId(question.options ?? [], 'opt').value) - 1));
    // (EQ-829) These lines are to maintain backward compatibility
    if (!question.code) {
      this.form.get('code').setValue(question.id);
    }

    this.patchStimulus(question.stimulus);
    this.patchVisibleWhen(question.visibleWhen);
  }

  protected patchBlockTerms(blockTerms: CustomTerm[]): void {
    if (!blockTerms?.length) {
      return;
    }

    (this.form.get('monitorTerms').get('blockedTerms') as FormArray).clear();
    blockTerms.forEach((term) =>
      (this.form.get('monitorTerms').get('blockedTerms') as FormArray).push(
        new FormGroup({ type: new FormControl(term.type), term: new FormControl(term.term) })
      )
    );
  }

  protected patchFlagTerms(flagTerms: CustomTerm[]): void {
    if (!flagTerms?.length) {
      return;
    }

    (this.form.get('monitorTerms').get('flaggedTerms') as FormArray).clear();
    flagTerms.forEach((term) =>
      (this.form.get('monitorTerms').get('flaggedTerms') as FormArray).push(
        new FormGroup({ type: new FormControl(term.type), term: new FormControl(term.term) })
      )
    );
  }

  private patchStimulus(stimulus: Media[]): void {
    if (!stimulus || stimulus.length === 0) {
      return;
    }

    stimulus.forEach((s) => {
      (this.form.get('stimulus') as FormArray).push(new MediaForm(s).form);
    });
  }

  private patchVisibleWhen(visibleWhen: (Condition | Command)[]): void {
    if (!visibleWhen || visibleWhen.length === 0) {
      return;
    }

    visibleWhen.forEach((condition) => {
      if (typeof condition === 'object') {
        (this.form.get('visibleWhen') as FormArray).push(new ConditionForm(condition as Condition).form);
      } else {
        (this.form.get('visibleWhen') as FormArray).push(new FormControl(condition as Command));
      }
    });
  }
}
